import { flatten, groupBy, keys, map, mapValues, sumBy, uniq } from 'lodash';
import { action, computed, observable, set } from 'mobx';
import { addDuration, autostore, Duration, getTotalSeconds, HttpClient, humanizeString, IConfig } from 'Mri.Web.Common';

export enum Labels {
    Offline = 'No or Limited Connectivity',
    Good = 'Good',
    Critical = 'Critical',
    Warning = 'Video On, Warning',
    Playing = 'Video On',
    NotPlaying = 'Video Off',
    TotalDisplays = 'Total Displays',
}

export class BiStore {
    public constructor(httpClient: HttpClient) {
        this.httpClient = httpClient;

        autostore(this.storage, 'BiStore.BiStorage', {
            useLocalStorage: true,
            useSessionStorage: true,
            useCustomStorage: false,
        });
    }

    @action
    public async loadStatusesAsync(model: Mri.Web.Portal.Controllers.BI.SearchModel, config?: IConfig) {
        const statuses = await this.httpClient.post<Mri.BI.ActiveDisplayLogSummaryDate[]>(
            `/api/v1/bi/status/date-summary`,
            model,
            config
        );
        this.statuses = statuses;
    }

    @action
    public async loadStatusDateTotalsAsync(model: Mri.Web.Portal.Controllers.BI.SearchModel, config?: IConfig) {
        const totals = await this.httpClient.post<Mri.Web.Portal.Controllers.BI.ActiveDisplayLogSummaryTotalModel[]>(
            `/api/v1/bi/status/date-summary/totals`,
            model,
            config
        );
        this.statusTotals = totals;
    }

    @action
    public async loadTotalsAsync(model: Mri.Web.Portal.Controllers.BI.SearchModel, config?: IConfig) {
        const totals = await this.httpClient.post<Mri.Web.Portal.Controllers.BI.StatusTotalModel>(
            `/api/v1/bi/status/totals`,
            model,
            config
        );
        this.totals = totals;
    }

    @action
    public async loadDisplayEventsAsync(model: Mri.Web.Portal.Controllers.BI.SearchModel, config?: IConfig) {
        const events = await this.httpClient.post<Mri.BI.DislpayEventSummaryDate[]>(
            `/api/v1/bi/display-events/date-summary`,
            model,
            config
        );
        this.displayEvents = events;
    }

    @action
    public async loadDisplayEventTotalsAsync(model: Mri.Web.Portal.Controllers.BI.SearchModel, config?: IConfig) {
        const totals = await this.httpClient.post<Mri.Web.Portal.Controllers.BI.DisplayEventSummaryTotalModel[]>(
            `/api/v1/bi/display-events/date-summary/totals`,
            model,
            config
        );
        this.displayEventTotals = totals;
    }

    @action
    public async loadRuntimeStatisticsAsync(model: Mri.Web.Portal.Controllers.BI.SearchModel, config?: IConfig) {
        const statuses = await this.httpClient.post<Mri.BI.RuntimeStatisticsSummaryDate[]>(
            `/api/v1/bi/runtime-statistics/date-summary`,
            model,
            config
        );
        this.runtimeStatistics = statuses;
    }

    @action
    public async loadRuntimeStatisticsDateTotalsAsync(
        model: Mri.Web.Portal.Controllers.BI.SearchModel,
        config?: IConfig
    ) {
        const totals = await this.httpClient.post<Mri.Web.Portal.Controllers.BI.RuntimeStatisticsSummaryTotalModel[]>(
            `/api/v1/bi/runtime-statistics/date-summary/totals`,
            model,
            config
        );
        this.runtimeStatisticsTotals = totals;
    }

    @action
    public async loadTicketsReportAsync(model: Mri.Web.Portal.Controllers.BI.SearchModel, config?: IConfig) {
        const ticketReportModel = await this.httpClient.post<Mri.Web.Portal.Controllers.BI.TicketReportModel>(
            `/api/v1/bi/tickets/report`,
            model,
            config
        );
        if (ticketReportModel) {
            this.ticketReportModel = ticketReportModel;
        }
    }

    @action
    public async loadDisplayEventDateSummaryAsync(serialNumber: string, startDate: Date, endDate: Date) {
        const summary = await this.httpClient.get<Mri.BI.DislpayEventSummaryDate[]>(
            `/api/v1/bi/${serialNumber}/display-events/date-summary`,
            {
                params: {
                    endDate,
                    startDate,
                },
            }
        );
        if (summary) {
            this.displayEventSummaries.set(serialNumber, summary);
        }
    }

    @computed
    public get displayStatusTotal() {
        let showingVideo = 0;
        let noVideo = 0;
        let warning = 0;

        if (this.totals) {
            showingVideo = this.totals.showingVideo;
            warning = this.totals.warning;
            noVideo = this.totals.noVideo;
        }

        return {
            labels: [Labels.Playing, Labels.Warning, Labels.NotPlaying],
            data: [showingVideo, warning, noVideo],
        };
    }

    @computed
    public get displayTotalPlaying() {
        if (this.totals) {
            return {
                playingVideo: this.totals.showingVideo,
                total: this.totals.showingVideo + this.totals.noVideo,
            };
        }
    }

    @computed
    public get displayTotal() {
        if (this.totals) {
            return {
                total: this.totals.total,
            };
        }
    }

    @computed
    public get videoStatusTotal() {
        let showing = 0;
        let notShowing = 0;

        if (this.totals) {
            showing = this.totals.showingVideo;
            notShowing = this.totals.noVideo;
        }

        return {
            labels: [Labels.Playing, Labels.NotPlaying],
            data: [showing, notShowing],
        };
    }

    @computed
    public get displayEventBarData() {
        if (this.displayEventTotals) {
            const groups = groupBy(this.displayEventTotals, 'date');
            const orderedLabels = keys(groups).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

            return {
                labels: orderedLabels,
                data: orderedLabels.map(l => groups[l]).map(g => sumBy(g, v => v.displays.length)),
            };
        }
        return {
            labels: [],
            data: [],
        };
    }

    @computed
    public get displayHealthLineData() {
        if (this.statusTotals) {
            const groups = groupBy(this.statusTotals, 'date');
            const orderedLabels = keys(groups).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

            const good = this.statusTotals.filter(s => s.displayHealth == Mri.DisplayStatuses.DisplayHealth.Good);
            const critical = this.statusTotals.filter(
                s => s.displayHealth == Mri.DisplayStatuses.DisplayHealth.Critical
            );
            const notification = this.statusTotals.filter(
                s => s.displayHealth == Mri.DisplayStatuses.DisplayHealth.Notification
            );
            const warning = this.statusTotals.filter(s => s.displayHealth == Mri.DisplayStatuses.DisplayHealth.Warning);

            const goodGroup = groupBy(good, 'date');
            const criticalGroup = groupBy(critical, 'date');
            const notificationGroup = groupBy(notification, 'date');
            const warningGroup = groupBy(warning, 'date');

            return {
                labels: orderedLabels,
                goodData: orderedLabels.map(l => groups[l]).map(g => sumBy(g, 'numPolls')),
                criticalData: orderedLabels.map(l => groups[l]).map(g => sumBy(g, 'numPolls')),
                //notificationData: orderedLabels.map(l => groups[l]).map(g => sumBy(g, 'count')),
                warningData: orderedLabels.map(l => groups[l]).map(g => sumBy(g, 'numPolls')),
            };
        }
        return {
            labels: [],
            goodData: [],
            criticalData: [],
            //notificationData: [],
            warningData: [],
        };
    }

    @computed
    public get videoUptimeLineData() {
        if (this.runtimeStatisticsTotals && this.totals) {
            const groups = groupBy(this.runtimeStatisticsTotals, 'date');
            const orderedLabels = keys(groups).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

            const accumulations = orderedLabels
                .map(l => groups[l])
                .map(g =>
                    g.reduce(
                        (a, v) => {
                            // TODO how to handle double sided displays. How to handle backup/failover displays
                            return {
                                total: addDuration(a.total, v.timeRunning),
                                playing: addDuration(a.playing, addDuration(v.videoInputValid, v.videoInputStalled)),
                            };
                        },
                        {
                            total: new Duration(0),
                            playing: new Duration(0),
                        }
                    )
                );

            const totalSeconds = (this.totals.noVideo + this.totals.showingVideo) * 86400;

            return {
                labels: orderedLabels,
                percentPlaying: accumulations.map(g => (getTotalSeconds(g.playing) / totalSeconds) * 100),
            };
        }
        return {
            labels: [],
            percentPlaying: [],
        };
    }

    @computed
    public get sortedDisplayEventsTotals() {
        if (this.displayEventTotals) {
            const groups = groupBy(this.displayEventTotals, 'downTimeIncidentType');

            const sums = mapValues(groups, g => {
                return uniq(flatten(g.map(v => flatten(v.displays))));
            });

            const objects = map(sums, (v, k) => {
                return {
                    downtimeIncidentType: Number(k) as Mri.DisplayApi.DowntimeIncidentTypes,
                    displays: v,
                };
            });
            const sorted = objects.sort((a, b) => b.displays.length - a.displays.length);

            return sorted;
        }
        return [];
    }

    @observable
    public ticketReportModel!: Mri.Web.Portal.Controllers.BI.TicketReportModel;
    @observable
    public displayEventSummaries = new Map<string, Mri.BI.DislpayEventSummaryDate[]>();
    @observable
    public statuses?: Mri.BI.ActiveDisplayLogSummaryDate[];
    @observable
    public statusTotals?: Mri.Web.Portal.Controllers.BI.ActiveDisplayLogSummaryTotalModel[];
    @observable
    public totals?: Mri.Web.Portal.Controllers.BI.StatusTotalModel;
    @observable
    public displayEvents?: Mri.BI.DislpayEventSummaryDate[];
    @observable
    public displayEventTotals?: Mri.Web.Portal.Controllers.BI.DisplayEventSummaryTotalModel[];
    @observable
    public runtimeStatistics?: Mri.BI.RuntimeStatisticsSummaryDate[];
    @observable
    public runtimeStatisticsTotals?: Mri.Web.Portal.Controllers.BI.RuntimeStatisticsSummaryTotalModel[];
    @observable
    public storage = new BiStorage();
    @observable
    public dashboardDisplaysTitle: string | Labels | undefined = undefined;
    private readonly httpClient: HttpClient;
}

class BiStorage {
    public static version = 3;

    public isValid() {
        return this.version == BiStorage.version;
    }

    @observable
    public version = BiStorage.version;
    @observable
    public dateRangeOption: string = '';
}
